import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {RootState} from '../store';

// Define a type for the slice state
interface Patient {
    id: number;
    firstName: string;
    lastName: string;
    fullName: string;
    birthdate: string;
}

interface PatientState {
    currentPatient: Patient;
    subPatients: Patient[];
}

const getPatientStorage = (): Storage | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        if (sessionStorage.getItem('auth_session_only') === '1') {
            return sessionStorage;
        }
    } catch {
        // Ignore sessionStorage errors
    }
    return localStorage;
};

const persistCurrentPatient = (patient: Patient) => {
    const storage = getPatientStorage();
    if (!storage) return;
    try {
        storage.setItem('currentPatient', JSON.stringify(patient));
    } catch {
        // Silently handle storage save errors
    }
};

const restoreCurrentPatient = (): Patient | null => {
    const storage = getPatientStorage();
    if (!storage) return null;
    try {
        const storedPatient = storage.getItem('currentPatient');
        if (!storedPatient) return null;
        const patient = JSON.parse(storedPatient);
        if (patient.id && patient.id > 0 && patient.firstName && patient.lastName) {
            return patient as Patient;
        }
    } catch {
        // Silently handle storage restore errors
    }
    return null;
};

// Define the initial state using that type
const initialState: PatientState = {
    currentPatient: {id: 0, firstName: '', lastName: '', fullName: '', birthdate: ''},
    subPatients: [],
};

export const patientSlice = createSlice({
    name: 'patient',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setCurrentPatient: (state, action: PayloadAction<Patient>) => {
            Object.assign(state.currentPatient, action.payload);
            // Persist current patient to storage for token refresh scenarios.
            persistCurrentPatient(action.payload);
        },
        setSubPatients: (state, action: PayloadAction<Patient[]>) => {
            //Object.assign(state.subPatients, action.payload);
            state.subPatients = action.payload;
        },
        restoreCurrentPatientFromStorage: (state) => {
            const patient = restoreCurrentPatient();
            if (patient) {
                Object.assign(state.currentPatient, patient);
            }
        },
        resetPatientState: (state) => {
            state.currentPatient = {id: 0, firstName: '', lastName: '', fullName: '', birthdate: ''};
            state.subPatients = [];
        },
    },
});

export const {setCurrentPatient, setSubPatients, restoreCurrentPatientFromStorage, resetPatientState} =
    patientSlice.actions;

export const selectCurrentPatient = (state: RootState) => state.patient.currentPatient;
export const selectFullname = (state: RootState) => state.patient.currentPatient?.fullName;
export const selectSubPatients = (state: RootState) => state.patient.subPatients;

export default patientSlice.reducer;
